import { certificationTypeToHudVoucherTableLabel } from '../../../utils/certificationTypeLabel';
import { TRANSFER_CERTIFICATION_TYPES } from '../../GeneralAffordable/constants';

const DRAFT_STATUSES = new Set(['draft', 'error']);
const SNAPSHOT_STATUSES = new Set(['submitted', 'approved', 'paid']);

const VALIDATION_STATE_MISSING_REQUIRED = 'missing_required_amount_inputs';

const TURNOVER_CODE_TO_DIRECTION = { O: 'OUT', I: 'IN' };

// Mirrors the Adjustments grid Cert Type column (HUD-52670-A Part 3): UT/AR/IR/IC + -O/-I.
const deriveTurnoverCodeLabel = (row) => {
  const turnoverCode = row?.turnoverCode || null;
  const direction = TURNOVER_CODE_TO_DIRECTION[turnoverCode];
  if (
    direction &&
    TRANSFER_CERTIFICATION_TYPES.includes(row?.certificationType)
  ) {
    return certificationTypeToHudVoucherTableLabel(
      row.certificationType,
      direction,
    );
  }
  return turnoverCode;
};

const getNormalizedSubmissionStatus = (submissionStatus) =>
  String(submissionStatus || '')
    .trim()
    .toLowerCase();

const parseUnitSortParts = (unitValue) => {
  const normalized = String(unitValue || '').trim();
  const match = normalized.match(/^(\d+)(.*)$/);
  if (!match) {
    return {
      hasNumericPrefix: false,
      numericPrefix: Number.POSITIVE_INFINITY,
      suffix: normalized.toLowerCase(),
    };
  }

  return {
    hasNumericPrefix: true,
    numericPrefix: Number(match[1]),
    suffix: String(match[2] || '')
      .trim()
      .toLowerCase(),
  };
};

export const compareUnitNumbers = (left, right) => {
  const leftParts = parseUnitSortParts(left?.unitNumber || left?.unit);
  const rightParts = parseUnitSortParts(right?.unitNumber || right?.unit);

  if (leftParts.hasNumericPrefix !== rightParts.hasNumericPrefix) {
    return leftParts.hasNumericPrefix ? -1 : 1;
  }

  if (leftParts.numericPrefix !== rightParts.numericPrefix) {
    return leftParts.numericPrefix - rightParts.numericPrefix;
  }

  if (leftParts.suffix !== rightParts.suffix) {
    return leftParts.suffix.localeCompare(rightParts.suffix);
  }

  return String(left?.unitId || '').localeCompare(String(right?.unitId || ''));
};

const normalizeStatusLabel = ({ submissionStatus, rawStatus }) => {
  const normalizedSubmissionStatus =
    getNormalizedSubmissionStatus(submissionStatus);
  if (SNAPSHOT_STATUSES.has(normalizedSubmissionStatus)) {
    return 'Submitted';
  }

  if (DRAFT_STATUSES.has(normalizedSubmissionStatus)) {
    return 'Not Submitted';
  }

  if (rawStatus) {
    return mapApiRowStatusToDisplay(rawStatus);
  }

  return 'Not Submitted';
};

/** Backend may send NOT_SUBMITTED / SUBMITTED on row.status */
const mapApiRowStatusToDisplay = (status) => {
  const normalized = String(status || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_');
  if (normalized === 'NOT_SUBMITTED') return 'Not Submitted';
  if (normalized === 'SUBMITTED') return 'Submitted';
  return status;
};

const toNullableNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const toLocalDateFromDateLike = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  const normalized = String(value).trim();
  if (!normalized) return null;

  const dateOnlyMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnlyMatch) {
    const year = Number(dateOnlyMatch[1]);
    const month = Number(dateOnlyMatch[2]);
    const day = Number(dateOnlyMatch[3]);
    return new Date(year, month - 1, day);
  }

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return null;

  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

/**
 * GET regular-assistance may return a JSON array, or { rows, submissionStatus }.
 */
export const getRawLinesFromRegularAssistanceResponse = (response) => {
  if (Array.isArray(response)) {
    return response;
  }
  if (response && Array.isArray(response.rows)) {
    return response.rows;
  }
  return [];
};

const normalizeRow = ({ row, submissionStatus }) => {
  const headDisplay = String(
    row?.headOfHouseholdDisplay ||
      row?.headOfHouseholdName ||
      row?.headOfHousehold ||
      row?.hohName ||
      '',
  );
  const isVacant = Boolean(
    row?.lineCategory === 'vacant' ||
      row?.isVacant ||
      row?.occupancyStatus === 'Vacant' ||
      row?.occupancyStatus === 'VACANT' ||
      headDisplay.trim().toLowerCase() === 'vacant',
  );
  const requestedAmount =
    toNullableNumber(
      row?.requestedAmount ?? row?.assistancePayment ?? row?.requested,
    ) ?? 0;
  const validationState = String(row?.validationState || '').toLowerCase();
  const payable = row?.payable;
  const nextRecertDate = row?.nextRecertDueDate ?? null;

  return {
    id: row?.id || `${row?.unitId || row?.unitNumber || 'row'}`,
    unitId: row?.unitId || '',
    headOfHouseholdResidentId: row?.headOfHouseholdResidentId || '',
    status: normalizeStatusLabel({
      submissionStatus,
      rawStatus: row?.statusLabel || row?.status,
    }),
    headOfHousehold: isVacant ? 'Vacant' : headDisplay,
    unit: row?.unitNumber || row?.unit || '',
    hudUnitNumber: row?.hudUnitNumber || '',
    bedsBaths:
      row?.bedsBathsDisplay ||
      row?.bedsBaths ||
      (row?.unitSize || row?.unitBaths
        ? `${row?.unitSize ?? 0}/${row?.unitBaths ?? 0}`
        : ''),
    contractRent: toNullableNumber(
      row?.contractRent ?? row?.floorplanQuotedRentAmount,
    ),
    utilityAllowance: toNullableNumber(
      row?.utilityAllowance ?? row?.floorplanHudUtilityAllowance,
    ),
    grossRent: toNullableNumber(
      row?.grossRent ?? row?.floorplanHudGrossRentLimit,
    ),
    incomeCode: row?.incomeCode || null,
    requestedAmount: isVacant ? 0 : requestedAmount,
    validationState: row?.validationState || null,
    changeCode: row?.changeCode || null,
    turnoverCode: deriveTurnoverCodeLabel(row),
    turnoverMonthDay: toLocalDateFromDateLike(row?.turnoverMonthDay),
    dateFirstRecertReminderNoticeSent:
      row?.dateFirstRecertReminderNoticeSent || null,
    payable: payable === undefined ? null : Boolean(payable),
    nextRecertDate,
    approvedAmount: toNullableNumber(row?.approvedAmount),
    isVacant,
    isValidationBlocking:
      validationState === VALIDATION_STATE_MISSING_REQUIRED ||
      payable === false ||
      Boolean(row?.isSubmissionBlocking),
    blockingReason:
      row?.submissionBlockingReason ||
      row?.validationMessage ||
      row?.blockingReason ||
      null,
  };
};

export const buildRegularAssistanceViewModel = ({
  voucher,
  regularAssistanceResponse,
}) => {
  const submissionStatus =
    (!Array.isArray(regularAssistanceResponse) &&
      regularAssistanceResponse?.submissionStatus) ||
    voucher?.submissionStatus ||
    'Draft';
  const normalizedSubmissionStatus =
    getNormalizedSubmissionStatus(submissionStatus);
  const isSnapshotMode = SNAPSHOT_STATUSES.has(normalizedSubmissionStatus);
  const rawLines = getRawLinesFromRegularAssistanceResponse(
    regularAssistanceResponse,
  );
  const rows = rawLines.map((row) => normalizeRow({ row, submissionStatus }));

  const hasValidationErrors = rows.some((row) => row.isValidationBlocking);
  const hasUnsubmittedEligibleEvents = Boolean(
    voucher?.hasUnsubmittedEligibleEvents,
  );
  const backendBlockingReasons = Array.isArray(voucher?.submissionBlockers)
    ? voucher.submissionBlockers
    : [];
  const submissionBlockers = [
    ...(hasUnsubmittedEligibleEvents ? ['UNSUBMITTED_ELIGIBLE_EVENTS'] : []),
    ...(hasValidationErrors
      ? ['REGULAR_ASSISTANCE_VALIDATION_OR_PAYABLE']
      : []),
    ...backendBlockingReasons,
  ];

  return {
    rows,
    isSnapshotMode,
    canDerive: DRAFT_STATUSES.has(normalizedSubmissionStatus),
    deriveDisabledReason: isSnapshotMode ? 'SNAPSHOT_MODE' : null,
    submissionBlockers,
    hasValidationErrors,
    hasUnsubmittedEligibleEvents,
  };
};
